"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.aggregateHVACGroups = void 0;
const types_1 = require("../../types");
const alertStatus_1 = require("../alertStatus");
const coverage_1 = require("../coverage");
const eerDisplayMode_1 = require("../hvac/eerDisplayMode");
const plausibility_1 = require("../plausibility");
const sensorClassification_1 = require("../sensorClassification");
/**
 * Pure HVAC aggregation. Extracted verbatim from `useHVACAggregation`'s
 * `useMemo` body.
 *
 * HVAC is group-shaped, not sensor-shaped: one row per system, each backed by
 * an air handler + condenser pair, so its offline state is an OR across two
 * physical sensors.
 */
function aggregateHVACGroups(hvacSensorGroups, seerResults, alertResults, filterResults, historicalResults, loading, groupsLoading, nowSeconds) {
    if (hvacSensorGroups.length === 0) {
        return {
            sensors: [],
            sensorCount: 0,
            overallStatus: types_1.SensorCardStatus.Good,
            needsAttentionCount: 0,
            activeAlertCount: 0,
            offlineCount: 0,
            unknownCount: 0,
            coverage: 'full',
            averages: {
                avgEER: 0,
                avgEERLabel: 'No Systems',
                runningCount: 0,
                offCount: 0,
                systemCount: 0,
                offlineCount: 0,
                isHistorical: false,
                avgFilterHealth: 0,
                avgFilterHealthLabel: 'No Systems',
                avgFilterHealthStatus: types_1.SensorCardStatus.Good,
                filterCount: 0,
            },
            loading: loading || groupsLoading,
        };
    }
    const sensorSummaries = [];
    let overallStatus = types_1.SensorCardStatus.Good;
    let worstSensor;
    let needsAttentionCount = 0;
    let activeAlertCount = 0;
    let eerSum = 0;
    let eerCount = 0;
    // Running groups whose EER was thrown out as implausible. Distinct from
    // eerCount === 0, which the label branch reads as "starting up / off".
    let implausibleEerCount = 0;
    let offEerSum = 0;
    let offEerCount = 0;
    let runningCount = 0;
    let offCount = 0;
    let offlineCount = 0;
    let filterHealthSum = 0;
    let filterHealthCount = 0;
    let uncalibratedFilterCount = 0;
    let offlineFilterCount = 0;
    hvacSensorGroups.forEach((group) => {
        const seerData = seerResults.get(group.groupId) ?? null;
        const isRunning = seerData?.hvacRunning ?? false;
        const isVariableSpeed = group.outdoorSensor?.equipmentDetails?.isVariableSpeed ?? false;
        const historicalEer = historicalResults.get(group.groupId) ?? 0;
        // Either half of the pair going silent breaks EER math and makes the
        // resolver read "off" — surface the outage instead of "A/C Off". Coverage
        // is group-shaped: one row per system, so an OR across the two physical
        // members, worst wins.
        const memberCoverage = [group.indoorSensor, group.outdoorSensor]
            .filter(Boolean)
            .map((member) => (0, coverage_1.sensorCoverageState)(member, nowSeconds));
        const coverage = memberCoverage.includes('offline')
            ? 'offline'
            : memberCoverage.includes('never_paired')
                ? 'never_paired'
                : memberCoverage.includes('awaiting_data')
                    ? 'awaiting_data'
                    : 'covered';
        const isOffline = coverage === 'offline';
        let status;
        let value;
        let needsAttention = false;
        let attentionMessage;
        let typeLabel;
        const groupName = group.indoorSensor?.location ||
            group.indoorSensor?.name ||
            group.outdoorSensor?.location ||
            group.outdoorSensor?.name ||
            `System ${group.groupId.slice(0, 6)}`;
        if (isOffline) {
            offlineCount++;
            // Offline, not Error — a coverage gap, not a hazard. See water.ts.
            status = types_1.SensorCardStatus.Offline;
            value = 'Offline';
            // typeLabel keeps the real state rather than being overwritten with
            // 'Offline'. SensorOverviewBar renders `Offline · {typeLabel}`, so the
            // old assignment made the hub read "Offline · Offline".
            typeLabel = isRunning ? 'Running' : 'System Off';
            // No needsAttention / attentionMessage — the coverage badge owns this
            // row. See water.ts.
        }
        else if (isRunning) {
            runningCount++;
            // Check display mode to handle fan-only startup and variable speed grace
            const displayMode = (0, eerDisplayMode_1.determineEERDisplayMode)(seerData?.lastMinuteSeerRating, {
                isVariableSpeed,
                currentRunningPeriodStart: seerData?.currentRunningPeriodStart,
            });
            if (displayMode.mode === 'grace' || displayMode.mode === 'off') {
                // Grace period OR transient startup (resolver says running but CT data
                // hasn't arrived yet) — don't use current EER, fall back to historical
                status = types_1.SensorCardStatus.Good;
                value = displayMode.mode === 'grace' ? 'Waiting...' : 'Starting...';
                typeLabel = 'Starting...';
                if (historicalEer > 0) {
                    offEerSum += historicalEer;
                    offEerCount++;
                }
            }
            else if (displayMode.mode === 'peak') {
                // Fan-only startup — don't use bad current EER
                status = historicalEer > 0 ? (0, sensorClassification_1.eerToStatus)(historicalEer) : types_1.SensorCardStatus.Good;
                value = historicalEer > 0 ? `EER ${historicalEer.toFixed(1)}` : 'Starting...';
                typeLabel = 'Starting...';
                if (historicalEer > 0) {
                    offEerSum += historicalEer;
                    offEerCount++;
                }
            }
            else {
                // Normal running — use hourly average EER (matches "Avg EER" label on dashboard)
                const runningHourValue = parseFloat(seerData?.runningHourSeerRating?.seerValue ?? '0');
                const eerValue = runningHourValue > 0 ? runningHourValue : (0, sensorClassification_1.parseSeerValue)(seerData);
                // 0 stays the existing "no reading yet" sentinel; anything else outside
                // the plausible band is a sensor fault, not an efficiency verdict. Both
                // directions have to be overridden: eerToStatus(1.9) reads Error (a
                // hazard colour we have not earned) and eerToStatus(45) reads Good.
                const eerImplausible = eerValue > 0 && !(0, plausibility_1.isPlausible)('eer', eerValue);
                // Below the floor and above the ceiling are different statements. 45 is
                // a number no coil can produce — the instrument is wrong. 2.3 with the
                // compressor drawing 20 A all hour is either a coil that barely cools
                // or a misplaced probe; a service visit checks both, so that copy asks
                // for one instead of blaming the sensor. Both stay out of the average.
                const eerTooLow = eerImplausible && eerValue < plausibility_1.PLAUSIBLE_RANGES.eer.min;
                typeLabel = 'Running';
                if (eerImplausible) {
                    status = types_1.SensorCardStatus.Poor;
                    value = `EER ${plausibility_1.INVALID_READING_VALUE}`;
                    needsAttention = true;
                    attentionMessage = `${groupName}: ${eerTooLow ? plausibility_1.LOW_EFFICIENCY_LABEL : plausibility_1.INVALID_READING_LABEL}`;
                    implausibleEerCount++;
                }
                else {
                    status = (0, sensorClassification_1.eerToStatus)(eerValue);
                    value = `EER ${eerValue.toFixed(1)}`;
                    if (eerValue > 0) {
                        eerSum += eerValue;
                        eerCount++;
                    }
                    if (status !== types_1.SensorCardStatus.Good) {
                        needsAttention = true;
                        // This state is derived from the live EER band alone — no alert row
                        // backs it — so the pill must read as a status, not an alert.
                        attentionMessage = `${groupName}: Poor`;
                    }
                }
            }
        }
        else {
            offCount++;
            status = types_1.SensorCardStatus.Good; // Off is not a bad state
            value = 'Off';
            typeLabel = 'System Off';
            // Use stored historical EER for off units (stable, no decay)
            if (historicalEer > 0) {
                offEerSum += historicalEer;
                offEerCount++;
            }
        }
        // Filter health for this system's air handler. Mirrors the branch order
        // useAirAggregation used before the move: offline > just-replaced >
        // never-calibrated > Lambda health percentage.
        const airHandler = group.indoorSensor;
        const healthPct = filterResults.get(group.groupId)?.filterHealthPercentage;
        if (isOffline) {
            offlineFilterCount++;
        }
        else if (airHandler?.pendingBaselineCapture) {
            // Installer pressed "Filter replaced" — show a fresh filter now, even
            // though the Lambda needs the next A/C cycle to capture a baseline.
            filterHealthSum += 100;
            filterHealthCount++;
        }
        else if (!airHandler || airHandler.startingStaticPressure == null) {
            uncalibratedFilterCount++;
        }
        else if (healthPct !== undefined && healthPct !== null) {
            filterHealthSum += healthPct;
            filterHealthCount++;
            const filterStatus = (0, sensorClassification_1.filterHealthToStatus)(healthPct);
            status = (0, sensorClassification_1.worstStatus)(status, filterStatus);
            if (filterStatus !== types_1.SensorCardStatus.Good) {
                needsAttention = true;
                if (!attentionMessage) {
                    attentionMessage = `${groupName} filter: ${Math.round(healthPct)}%`;
                }
            }
        }
        // Alerts are applied AFTER the reading-derived verdict and can only make
        // it worse. A system sitting idle is legitimately "Off" — but "Off" is
        // not "fine", and three open faults have to outrank a green EER band.
        //
        // STATUS folds every active alert (type-aware — a member's SENSOR_INACTIVE
        // lands on Offline). The HAZARD channel (counts + badge copy) excludes those
        // rows: a dead pair carries one per member, which read "2 alerts · 1 of 2
        // systems" in hazard amber directly above the coverage badge. See water.ts.
        const groupAlerts = alertResults.get(group.groupId) ?? [];
        const groupHazards = (0, alertStatus_1.hazardAlerts)(groupAlerts);
        activeAlertCount += groupHazards.length;
        if (groupAlerts.length > 0) {
            status = (0, sensorClassification_1.worstStatus)(status, (0, alertStatus_1.activeAlertsToStatus)(groupAlerts));
        }
        if (groupHazards.length > 0) {
            needsAttention = true;
            attentionMessage = (0, alertStatus_1.alertAttentionMessage)(groupName, groupHazards);
        }
        // Per-member rows so the UI can show WHICH half of the pair is offline
        // and route per-sensor settings (labels match installer vocabulary).
        const members = [];
        if (group.indoorSensor) {
            members.push({
                sensorId: group.indoorSensor.id,
                label: 'Air Handler',
                location: group.indoorSensor.location || undefined,
                isOffline: (0, coverage_1.sensorCoverageState)(group.indoorSensor, nowSeconds) === 'offline',
            });
        }
        if (group.outdoorSensor) {
            members.push({
                sensorId: group.outdoorSensor.id,
                label: 'Condenser',
                location: group.outdoorSensor.location || undefined,
                isOffline: (0, coverage_1.sensorCoverageState)(group.outdoorSensor, nowSeconds) === 'offline',
            });
        }
        const summary = {
            sensorId: group.indoorSensor?.id ?? group.outdoorSensor?.id ?? group.groupId,
            name: groupName,
            location: group.indoorSensor?.location || group.outdoorSensor?.location || '',
            typeLabel,
            sensorType: 'HVAC_GROUP',
            status,
            value,
            needsAttention,
            attentionMessage,
            isOffline,
            coverage,
            lastReportedAt: Math.max(Number(group.indoorSensor?.lastReported ?? 0), Number(group.outdoorSensor?.lastReported ?? 0)),
            alertCount: groupHazards.length,
            groupId: group.groupId,
            members,
        };
        sensorSummaries.push(summary);
        if (needsAttention)
            needsAttentionCount++;
        const newOverall = (0, sensorClassification_1.worstStatus)(overallStatus, status);
        if (newOverall !== overallStatus) {
            overallStatus = newOverall;
            worstSensor = summary;
        }
    });
    // Determine avg EER: prefer running units, fall back to historical from off units
    let avgEER;
    let avgEERLabel;
    let isHistorical;
    if (eerCount > 0) {
        avgEER = Math.round((eerSum / eerCount) * 10) / 10;
        avgEERLabel = `${(0, sensorClassification_1.eerToLabel)(avgEER)} (${avgEER.toFixed(1)})`;
        isHistorical = false;
    }
    else if (offEerCount > 0) {
        avgEER = Math.round((offEerSum / offEerCount) * 10) / 10;
        avgEERLabel = `${(0, sensorClassification_1.eerToLabel)(avgEER)} (${avgEER.toFixed(1)})`;
        isHistorical = true;
    }
    else {
        avgEER = 0;
        // "No valid reading because the sensor lied" is not "nothing is running
        // yet" — without implausibleEerCount a garbage EER would quietly render as
        // 'Starting...' and the homeowner would never learn the sensor needs
        // service.
        avgEERLabel =
            hvacSensorGroups.length > 0 && offlineCount === hvacSensorGroups.length
                ? 'Not monitored'
                : implausibleEerCount > 0
                    ? plausibility_1.INVALID_READING_VALUE
                    : runningCount > 0 ? 'Starting...' : 'A/C Off';
        isHistorical = false;
    }
    // Filter health average — same two-tier labeling as everywhere else:
    // "Offline" when every system's air handler is dead, "Not Calibrated"
    // when no installer has ever pressed a baseline, "No Systems"/"No Data"
    // otherwise. Never fall through to a bare "Good".
    const avgFilterHealth = filterHealthCount > 0 ? Math.round(filterHealthSum / filterHealthCount) : 0;
    const allFiltersOffline = hvacSensorGroups.length > 0 && offlineFilterCount === hvacSensorGroups.length;
    const allFiltersUncalibrated = hvacSensorGroups.length > 0 && uncalibratedFilterCount === hvacSensorGroups.length;
    const avgFilterHealthLabel = allFiltersOffline
        ? 'Not monitored'
        : allFiltersUncalibrated
            ? 'Not Calibrated'
            : filterHealthCount > 0
                ? `${(0, sensorClassification_1.filterHealthToLabel)(avgFilterHealth)} (${avgFilterHealth}%)`
                : 'No Data';
    const avgFilterHealthStatus = allFiltersOffline
        // Offline, not Error: nobody's filter is clogged, we just can't measure.
        ? types_1.SensorCardStatus.Offline
        : filterHealthCount > 0
            ? (0, sensorClassification_1.filterHealthToStatus)(avgFilterHealth)
            : types_1.SensorCardStatus.Good;
    // Rolled up from the per-row coverage states — one source of truth.
    const rollup = (0, coverage_1.categoryCoverage)(sensorSummaries);
    return {
        sensors: sensorSummaries,
        sensorCount: hvacSensorGroups.length,
        overallStatus,
        needsAttentionCount,
        activeAlertCount,
        offlineCount: rollup.offlineCount,
        unknownCount: rollup.unknownCount,
        coverage: rollup.coverage,
        worstSensor,
        averages: {
            avgEER,
            avgEERLabel,
            runningCount,
            offCount,
            systemCount: hvacSensorGroups.length,
            offlineCount,
            isHistorical,
            avgFilterHealth,
            avgFilterHealthLabel,
            avgFilterHealthStatus,
            // Physical filters, not readings: an offline or never-calibrated air
            // handler still has a filter in it, so it still counts.
            filterCount: hvacSensorGroups.filter((g) => g.indoorSensor).length,
        },
        loading: loading || groupsLoading,
    };
}
exports.aggregateHVACGroups = aggregateHVACGroups;
